import React, { useState, useEffect } from 'react';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import L, { LatLng } from 'leaflet';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import styled from 'styled-components';
import 'translations/translations';
import { StackCoordinateResponse, StackStateEnumDto } from '.generated/api';

import { StacksMapInfoCard } from 'features/stacks/StacksMapInfoCard';

import {
  primaryColor as measured,
  warningColor as notMeasured,
  buttonPrimaryColor,
} from 'theme/theme';
import 'leaflet/dist/leaflet.css';
import 'theme/leafletFade.css';
import { stackActions } from 'actions/stackActions';

const useStyles = makeStyles({
  mapBottomPadding: {
    // paddingBottom: '1.5rem'
  },
  mapSizing: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    zIndex: 1,
  },
});

const StyledPop = styled(Popup)`
  .leaflet-popup-content-wrapper {
    border-radius: unset;
    background: unset;
    box-shadow: unset;
  }
  .leaflet-popup-tip-container {
    opacity: 0;
  }
`;

type StackWithPosition = {
  coordinate: StackCoordinateResponse;
  position: L.LatLng;
};

interface FitBoundsProps {
  bounds: L.LatLngBounds;
}

// FitBounds component is a hack to update map bounds, since MapContainer will not update bounds when bounds property changes.
function FitBounds({ bounds }: FitBoundsProps) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, bounds]);

  return null;
}

interface StacksMapProps {
  disableStackDetailLinks?: boolean;
}

const StacksMap: React.FC<StacksMapProps> = ({ disableStackDetailLinks }) => {
  const dispatch = useDispatch();
  let timeout: number | undefined;
  // eslint-disable-next-line @typescript-eslint/ban-types
  const debounce = (func: Function, wait: number) => {
    clearTimeout(timeout);
    timeout = setTimeout(func, wait);
  };
  const classes = useStyles();

  const coordinates = useSelector(
    (state: ApplicationState) => state.stacks?.coordinates,
  );

  const [stacks, setStacks] = useState<StackWithPosition[]>([]);

  const worldBounds = new L.LatLngBounds([60.0, -150.0], [-60.0, 150.0]);
  const [mapBounds, setMapBounds] = useState(worldBounds);

  const [selectedStack, setSelectedStack] = useState<
    StackWithPosition | undefined
  >(undefined);

  const [selectedStackMarkerColor, setSelectedStackMarkerColor] = useState<
    string | undefined
  >(notMeasured);

  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      const latitudes = coordinates.map((coordinate) => coordinate.latitude);
      const longitudes = coordinates.map((coordinate) => coordinate.longitude);
      const bounds = new L.LatLngBounds(
        [Math.min(...latitudes), Math.min(...longitudes)],
        [Math.max(...latitudes), Math.max(...longitudes)],
      );

      setStacks(
        coordinates.map(
          (coordinate): StackWithPosition => ({
            coordinate,
            position: new LatLng(coordinate.latitude, coordinate.longitude),
          }),
        ),
      );
      setMapBounds(bounds);
    } else {
      setStacks([]);
      setMapBounds(worldBounds);
    }

    setSelectedStack(undefined);
  }, [coordinates]);

  const showSelectedStackInfo = (newStack: StackWithPosition) => {
    dispatch(stackActions.getStackMapInfoById(newStack.coordinate.stackId));
    // reset to restart css animation
    setSelectedStack(undefined);
    if (newStack) {
      setSelectedStackMarkerColor(getMarkerColor(newStack));
      setSelectedStack(newStack);
    }
  };

  const closeSelectedStackInfo = () => {
    setSelectedStack(undefined);
  };

  const getMarkerColor = (stack: StackWithPosition) => {
    return stack.coordinate.state === StackStateEnumDto.PRE_REGISTRATION
      ? notMeasured
      : measured('100');
  };

  const getMarkerOpacity = (stack: StackWithPosition): number => {
    return stack.coordinate.stackId === selectedStack?.coordinate.stackId
      ? 1
      : 0;
  };

  return (
    <MapContainer
      id="stackMap"
      className={classes.mapSizing}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MarkerClusterGroup
        chunkedLoading
        showCoverageOnHover={true}
        polygonOptions={{
          color: buttonPrimaryColor,
          weight: 5,
          opacity: 1,
          fillOpacity: 0.4,
        }}
      >
        {stacks.map((stack: StackWithPosition, index) => {
          return (
            <CircleMarker
              key={stack.coordinate.stackId + `,${index}`}
              center={stack.position}
              pathOptions={{
                color: '#FFFFFF',
                opacity: getMarkerOpacity(stack),
                weight: 1.5,
                fillOpacity: 1,
                fillColor: getMarkerColor(stack),
              }}
              radius={8}
              interactive={true}
              eventHandlers={{
                click: () => showSelectedStackInfo(stack),
              }}
            >
              <StyledPop
                minWidth={470}
                closeButton={false}
                keepInView={false}
                key={`${stack.coordinate.stackId},${index}`}
                onOpen={() => {
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  debounce(function () {}, 50);
                }}
                onClose={() => {
                  debounce(function () {
                    closeSelectedStackInfo();
                  }, 50);
                }}
              >
                <StacksMapInfoCard
                  // selectedStack={stack.coordinate}
                  closeSelectedStackInfo={closeSelectedStackInfo}
                  disableStackDetailLink={disableStackDetailLinks}
                ></StacksMapInfoCard>
              </StyledPop>
            </CircleMarker>
          );
        })}
      </MarkerClusterGroup>
      {selectedStack !== undefined && (
        <CircleMarker
          center={selectedStack.position}
          pathOptions={{
            opacity: 0,
            color: selectedStackMarkerColor,
            fillOpacity: 0.4,
            fillColor: selectedStackMarkerColor,
          }}
          radius={18}
          interactive={false}
          className="selectedMarker"
        ></CircleMarker>
      )}
      <FitBounds bounds={mapBounds} />
    </MapContainer>
  );
};
export { StacksMap };
